import React from "react";
import "./ConversationHistory.css";

const formatDate = (isoString) => {
  if (!isoString) return "Just now";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "Unknown";
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const previewText = (conversation) => {
  if (!conversation?.messages?.length) return "No messages captured";
  const firstUser = conversation.messages.find((msg) => msg.role === "user");
  const baseText = firstUser?.text || conversation.messages[0]?.text || "";
  return baseText.length > 80 ? `${baseText.slice(0, 80)}…` : baseText;
};

const ConversationHistory = ({
  conversations = [],
  selectedId,
  onSelectConversation = () => {},
  onClear = () => {},
  onSave = () => {},
  activeTenant,
  historyLimit = 20,
}) => {
  const selectedConversation =
    conversations.find((conv) => conv.id === selectedId) ||
    conversations[0] ||
    null;

  return (
    <aside className="history-panel">
      <div className="history-header">
        <div>
          <p className="history-kicker">
            Local only · {activeTenant || "current tenant"}
          </p>
          <h3>Conversation History</h3>
        </div>

        <div className="history-actions">
          <button className="ghost-btn" onClick={onSave}>
            Save current
          </button>
          <button
            className="link-btn"
            onClick={onClear}
            disabled={!conversations.length}
          >
            Clear all
          </button>
        </div>
      </div>

      <div className="history-subheader">
        <span className="history-pill">
          {Math.min(conversations.length, historyLimit)}/{historyLimit} saved
        </span>
        <span className="history-note">
          Autosaves on reset/tenant switch. Stored in this browser only.
        </span>
      </div>

      <div className="history-body">
        <div className="history-list">
          {conversations.length === 0 && (
            <div className="history-empty">
              <p className="history-empty-title">No saved conversations yet</p>
              <p className="history-empty-copy">
                Save a chat to pin it here. History is stored only in this
                browser.
              </p>
            </div>
          )}

          {conversations.map((conv) => (
            <button
              key={conv.id}
              className={`history-list-item ${
                selectedConversation?.id === conv.id ? "is-active" : ""
              }`}
              onClick={() => onSelectConversation(conv.id)}
            >
              <div className="history-meta-row">
                <span className="history-tenant">{conv.tenant}</span>
                <span className="history-time">{formatDate(conv.savedAt)}</span>
              </div>
              <div className="history-preview">{previewText(conv)}</div>
            </button>
          ))}
        </div>

        <div className="history-detail">
          {selectedConversation ? (
            <div className="history-messages">
              {selectedConversation.messages.map((msg, idx) => (
                <div
                  key={msg.id || `${selectedConversation.id}-${idx}`}
                  className={`history-msg history-msg--${msg.role}`}
                >
                  <div className="history-msg-header">
                    <span className="history-role">
                      {msg.role === "user" ? "You" : "Agent"}
                    </span>
                    {msg.timestamp && (
                      <span className="history-timestamp">
                        {formatTime(msg.timestamp)}
                      </span>
                    )}
                  </div>
                  <p className="history-msg-text">{msg.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="history-empty">
              <p className="history-empty-title">Select a saved chat</p>
              <p className="history-empty-copy">
                Snapshots appear here with the full conversation transcript.
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default ConversationHistory;
